import "react-image-crop/dist/ReactCrop.css";
import React, { useEffect, useMemo, useRef, useState } from "react";
import _ from "lodash/fp";
import {
  Box,
  Button,
  IconButton,
  List,
  ListItem,
  Tooltip,
  Typography,
} from "@mui/material";
import { Delete as DeleteIcon, Plus as PlusIcon } from "@actinc/dls/icons/";
import { GridGenerator } from "@actinc/dls/components";
import ReactCrop from "react-image-crop";
import { useFieldArray, useFormContext } from "react-hook-form";

import {
  AlertDialog,
  ControlledFormInput,
  ControlledFormSelect,
  ControlledFormSwitch,
} from "../../../../components";

const HotspotInteractionForm = ({
  imageAssets = [],
  index,
  interaction,
  readonly,
}) => {
  const imgRef = useRef(null);
  // console.debug(
  //   "HotspotInteractionForm - index %s, interaction %o",
  //   index,
  //   interaction,
  // );

  const defaultCropState = {
    unit: "px", // Can be 'px' or '%'
    x: "0",
    y: "0",
    width: "10",
    height: "10",
  };

  const namePrefix = `item.interactions[${index}]`;

  const [alertDialogProps, setAlertDialogProps] = useState({ open: false });
  const [crop, setCrop] = useState();
  const [selectedElement, setSelectedElement] = useState(null);
  const [completedCrop, setCompletedCrop] = useState(defaultCropState);

  const { control, getValues, setValue, watch } = useFormContext();
  const backgroundFileName = watch(`${namePrefix}.backgroundFileName`);

  const {
    append: appendElement,
    fields: elements,
    remove: removeElement,
    update: updateElement,
  } = useFieldArray({
    control,
    name: `${namePrefix}.elements`,
  });

  const selectedBackgroundImage = useMemo(
    () => imageAssets.find(({ name }) => name === backgroundFileName),
    [backgroundFileName],
  );

  useEffect(() => {
    setValue(
      `${namePrefix}.backgroundHeight`,
      selectedBackgroundImage?.height || 0,
    );
    setValue(
      `${namePrefix}.backgroundWidth`,
      selectedBackgroundImage?.width || 0,
    );
  }, [selectedBackgroundImage]);

  const addElement = () => {
    appendElement({
      colorStyle: "blue",
      correctResponse: false,
      height: 10,
      identifier: `Element${elements.length + 1}`,
      left: 0,
      radius: 10,
      shape: "",
      top: 0,
      width: 10,
      xCoord: 10,
      yCoord: 10,
    });
  };
  const closePositioningDialog = (e) => {
    setAlertDialogProps({
      open: false,
    });
    if (e === false) return; // user clicks Cancel
    const { x = 0, y = 0 } = completedCrop;
    getValues(`${namePrefix}.elements`).map((element, k) => {
      if (element.identifier === selectedElement.identifier) {
        updateElement(k, {
          ...element,
          left: element.shape === "rectangle" ? x : null,
          top: element.shape === "rectangle" ? y : null,
          xCoord: element.shape === "circle" ? x + element.radius : null,
          yCoord: element.shape === "circle" ? y + element.radius : null,
        });
      }
    });
    setCrop(defaultCropState);
    setCompletedCrop(defaultCropState);
  };
  const deleteElement = (i) => () => {
    removeElement(i);
  };

  const imageList = imageAssets.map(({ name }) => ({
    label: name,
    value: name,
  }));

  return (
    <Box
      sx={{
        alignItems: "self-start",
        display: "flex",
        flexDirection: "column",
        flexGrow: 1,
      }}
    >
      <GridGenerator
        groups={[
          [
            {
              children: (
                <ControlledFormSelect
                  control={control}
                  defaultValue={interaction.backgroundFileName}
                  fullWidth={true}
                  id={`${namePrefix}.backgroundFileName`}
                  label="Background Image"
                  name={`${namePrefix}.backgroundFileName`}
                  options={imageList}
                  readonly={readonly}
                  rules={{
                    required: true,
                  }}
                />
              ),
              key: `${namePrefix}-backgroundFileName`,
            },
            {
              children: (
                <ControlledFormInput
                  control={control}
                  defaultValue={interaction.maxChoices}
                  fullWidth={true}
                  id={`${namePrefix}.maxChoices`}
                  label="Max Choices"
                  multiline={false}
                  name={`${namePrefix}.maxChoices`}
                  readonly={readonly}
                  rules={{
                    pattern: {
                      message:
                        "This field must contain a valid number between 0-99",
                      value: /^[1-9][0-9]?$|^99$|^0$/,
                    },
                    required: true,
                  }}
                />
              ),
              key: `${namePrefix}-maxChoices`,
            },
          ],
        ]}
      />

      <Typography variant="h4">Elements</Typography>
      <List
        sx={{
          width: "100%",
        }}
      >
        {!!elements &&
          elements.map((element, i) => {
            const { id } = element;
            const { height, left, radius, shape, top, width, xCoord, yCoord } =
              watch(`${namePrefix}.elements[${i}]`);

            const cropCoords =
              shape === "circle"
                ? {
                    height: radius,
                    width: radius,
                    x: xCoord - radius,
                    y: yCoord - radius,
                    unit: "px",
                  }
                : {
                    height,
                    width,
                    x: left,
                    y: top,
                    unit: "px",
                  };
            return (
              <ListItem key={i}>
                <Box
                  sx={{
                    border: "1px solid gray",
                    borderRadius: 3,
                    padding: 1,
                    marginBottom: 1,
                    width: "100%",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "row",
                      flexGrow: 1,
                    }}
                  >
                    <Typography
                      sx={{ flexGrow: 1 }}
                      variant="subtitle1"
                    >{`Element ${i + 1}`}</Typography>
                    <Box
                      sx={{
                        display: "flex",
                        flexGrow: 1,
                        justifyContent: "flex-end",
                      }}
                    >
                      {!readonly && (
                        <Tooltip title="Delete Element">
                          <IconButton
                            aria-label={`delete element ${i + 1}`}
                            disabled={elements.length <= 1}
                            onClick={deleteElement(i)}
                            size="large"
                            sx={{ padding: 1 }}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                  </Box>
                  <GridGenerator
                    groups={[
                      [
                        {
                          children: (
                            <ControlledFormSelect
                              control={control}
                              defaultValue={element.shape}
                              fullWidth={true}
                              id={`${namePrefix}.elements[${i}].shape`}
                              label="Shape"
                              name={`${namePrefix}.elements[${i}].shape`}
                              options={["circle", "rectangle"].map((value) => ({
                                label: _.capitalize(value),
                                value,
                              }))}
                              readonly={readonly}
                              rules={{
                                required: true,
                              }}
                            />
                          ),
                          key: `element-${id}-shape`,
                        },
                        {
                          children: (
                            <ControlledFormSelect
                              control={control}
                              defaultValue={element.colorStyle}
                              fullWidth={true}
                              id={`${namePrefix}.elements[${i}].colorStyle`}
                              label="Color"
                              name={`${namePrefix}.elements[${i}].colorStyle`}
                              options={["blue", "green", "grey", "red"].map(
                                (value) => ({
                                  label: _.capitalize(value),
                                  value,
                                }),
                              )}
                              readonly={readonly}
                            />
                          ),
                          key: `element-${id}-color-style`,
                        },
                        {
                          children: (
                            <Box
                              sx={{
                                float: "right",
                              }}
                            >
                              <ControlledFormSwitch
                                control={control}
                                defaultValue={false}
                                id={`${namePrefix}.elements[${i}].correctResponse`}
                                key={`${namePrefix}.elements[${i}].correctResponse`}
                                label="Correct"
                                name={`${namePrefix}.elements[${i}].correctResponse`}
                                readonly={readonly}
                              />
                            </Box>
                          ),
                          key: `element-${id}-correct-response`,
                        },
                      ],
                    ]}
                  ></GridGenerator>
                  <Box
                    sx={{
                      alignItems: "inherit",
                      display: "flex",
                      flexDirection: "row",
                      flexGrow: 1,
                    }}
                  >
                    {!!shape &&
                      (shape === "circle" ? (
                        <Box>
                          <ControlledFormInput
                            control={control}
                            defaultValue={element.radius}
                            id={`${namePrefix}.elements[${i}].radius`}
                            label="Radius"
                            name={`${namePrefix}.elements[${i}].radius`}
                            readonly={readonly}
                            rules={{
                              pattern: {
                                message:
                                  "This field must contain a valid number",
                                value: /^\d+$/,
                              },
                              required: true,
                            }}
                          />
                        </Box>
                      ) : (
                        <Box>
                          <GridGenerator
                            groups={[
                              [
                                {
                                  children: (
                                    <ControlledFormInput
                                      control={control}
                                      defaultValue={element.height}
                                      fullWidth={true}
                                      id={`${namePrefix}.elements[${i}].height`}
                                      label="Height"
                                      name={`${namePrefix}.elements[${i}].height`}
                                      readonly={readonly}
                                      rules={{
                                        pattern: {
                                          message:
                                            "This field must contain a valid number",
                                          value: /^\d+$/,
                                        },
                                        required: true,
                                      }}
                                    />
                                  ),
                                  key: `element-${id}-height`,
                                },
                                {
                                  children: (
                                    <ControlledFormInput
                                      control={control}
                                      defaultValue={element.width}
                                      fullWidth={true}
                                      id={`${namePrefix}.elements[${i}].width`}
                                      label="Width"
                                      name={`${namePrefix}.elements[${i}].width`}
                                      readonly={readonly}
                                      rules={{
                                        pattern: {
                                          message:
                                            "This field must contain a valid number",
                                          value: /^\d+$/,
                                        },
                                        required: true,
                                      }}
                                    />
                                  ),
                                  key: `element-${id}-width`,
                                },
                              ],
                            ]}
                          ></GridGenerator>
                        </Box>
                      ))}
                    <Box
                      sx={{
                        display: "flex",
                        flexGrow: 1,
                        justifyContent: "flex-end",
                      }}
                    >
                      <Button
                        disabled={!shape || !backgroundFileName}
                        onClick={() => {
                          setAlertDialogProps({
                            confirmButtonText: "Set Location",
                            dialogTitle: "Select Element Location",
                            open: true,
                          });
                          setCrop({
                            ...cropCoords,
                          });
                          setCompletedCrop({
                            ...cropCoords,
                          });
                          setSelectedElement(
                            watch(`${namePrefix}.elements[${i}]`),
                          );
                        }}
                        size="small"
                        sx={{
                          minWidth: 145,
                          marginTop: 3,
                          maxHeight: "40px",
                        }}
                      >
                        Position Element
                      </Button>
                    </Box>
                  </Box>
                </Box>
              </ListItem>
            );
          })}
      </List>
      {alertDialogProps.open && (
        <AlertDialog {...alertDialogProps} onClose={closePositioningDialog}>
          <>
            <ReactCrop
              circularCrop={selectedElement.shape === "circle"}
              crop={crop}
              disabled={readonly}
              locked={true}
              onChange={(crop, _percentageCrop) => setCrop(crop)}
              onComplete={(crop) => setCompletedCrop(crop)}
            >
              <img ref={imgRef} alt="crop" src={selectedBackgroundImage?.src} />
            </ReactCrop>
          </>
        </AlertDialog>
      )}

      {!readonly && (
        <Button
          onClick={addElement}
          size="small"
          startIcon={<PlusIcon />}
          variant="outlined"
        >
          Create Element
        </Button>
      )}
    </Box>
  );
};

export { HotspotInteractionForm };
